import { format, isToday } from 'date-fns';
import React, { useMemo } from 'react';
import { useParams } from 'react-router-dom';

import { AdminRolesEnum } from '@/dashboard/enums/admin-roles';
import { useAuth } from '@dashboard/context/AuthContext';
import { toast } from '@dashboard/context/ToastContext';
import { useLiftAiPause } from '@dashboard/hooks/queries/useAdminMutations';
import { useAdminAiUsageStore } from '@dashboard/hooks/queries/useAdminQueries';

import { DataGrid, DataGridColumnHeader } from '@dashboard/components/DataGrid';
import EmptyState from '@dashboard/components/EmptyState';
import Link from '@dashboard/components/Link';
import { Badge } from '@dashboard/components/ui/badge';
import { Button } from '@dashboard/components/ui/button';
import { Skeleton } from '@dashboard/components/ui/skeleton';

import AiBudgetForm from './AiBudgetForm';
import AiUsagePeriodSelect from './AiUsagePeriodSelect';
import AiUsageTurnsTable from './AiUsageTurnsTable';
import { AI_USAGE_PLAN_LABELS } from './aiUsageLabels';
import { formatMicros } from './aiUsagePeriod';
import useAiUsagePeriod from './useAiUsagePeriod';

type AiUsageStoreUser = {
  userId: string;
  email: string;
  name: string;
  costMicros: number;
  turns: number;
  offTopicTurns: number;
  pausedUntil: string | null;
};

type AiUsageStoreData = {
  seller: { _id: string; name: string; displayName?: string };
  plan: string;
  budgetMicros: number;
  planBudgetMicros: number;
  overrideUsd: number | null;
  monthCostMicros: number;
  costMicros: number;
  turns: number;
  users: AiUsageStoreUser[];
};

const empty = <span className="text-zinc-400 dark:text-muted-foreground">-</span>;

const MERCHANT_PAGE_ROLES = [AdminRolesEnum.ADMIN, AdminRolesEnum.SUPPORT];

const pausedUntilLabel = (pausedUntil: string) => {
  const date = new Date(pausedUntil);
  return `Paused until ${format(date, isToday(date) ? 'HH:mm' : 'd MMM, HH:mm')}`;
};

const AiUsageStore = () => {
  const { sellerId } = useParams<{ sellerId: string }>();
  const { user } = useAuth();
  const { period, range, setPeriod } = useAiUsagePeriod();

  const { data: store, isLoading } = useAdminAiUsageStore(sellerId, range) as {
    data?: AiUsageStoreData;
    isLoading: boolean;
  };

  const liftPause = useLiftAiPause({
    onSuccess: () => toast({ title: 'Pause lifted' }),
    onError: (error: any) =>
      toast({ title: "Couldn't lift the pause", description: error?.message, type: 'error' }),
  });
  const { mutate: lift } = liftPause;
  const liftingUserId = liftPause.isPending ? liftPause.variables?.userId : undefined;

  const columns = useMemo(
    () => [
      {
        accessorKey: 'email',
        header: ({ column }: any) => <DataGridColumnHeader column={column} title="Email" />,
        cell: ({ row }: any) => row.original.email || empty,
      },
      {
        accessorKey: 'name',
        header: ({ column }: any) => <DataGridColumnHeader column={column} title="Name" />,
        cell: ({ row }: any) => row.original.name || empty,
      },
      {
        accessorKey: 'costMicros',
        header: ({ column }: any) => (
          <DataGridColumnHeader column={column} title="Estimated spend" />
        ),
        cell: ({ row }: any) => formatMicros(row.original.costMicros),
      },
      {
        accessorKey: 'turns',
        header: ({ column }: any) => <DataGridColumnHeader column={column} title="Turns" />,
        cell: ({ row }: any) => row.original.turns.toLocaleString(),
      },
      {
        accessorKey: 'offTopicTurns',
        header: ({ column }: any) => <DataGridColumnHeader column={column} title="Off-topic" />,
        cell: ({ row }: any) => row.original.offTopicTurns.toLocaleString(),
      },
      {
        accessorKey: 'pausedUntil',
        header: ({ column }: any) => <DataGridColumnHeader column={column} title="Status" />,
        cell: ({ row }: any) => {
          const { userId, pausedUntil } = row.original as AiUsageStoreUser;
          if (!pausedUntil) return empty;
          return (
            <div className="flex items-center gap-x-2">
              <Badge variant="warning">{pausedUntilLabel(pausedUntil)}</Badge>
              <Button
                size="sm"
                variant="outline"
                disabled={userId === liftingUserId}
                onClick={() => lift({ userId })}
              >
                Lift pause
              </Button>
            </div>
          );
        },
      },
    ],
    [lift, liftingUserId],
  );

  return (
    <div className="pr-4 space-y-6">
      <div className="flex items-start justify-between gap-x-4">
        <div className="space-y-2">
          {store ? (
            <h1>{store.seller.displayName || store.seller.name}</h1>
          ) : (
            isLoading && <Skeleton className="h-8 w-48" />
          )}
          {store && (
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-gray-500 dark:text-muted-foreground">
              <Badge variant="outline">{AI_USAGE_PLAN_LABELS[store.plan] ?? store.plan}</Badge>
              <span>
                {`${formatMicros(store.monthCostMicros)} of ${formatMicros(store.budgetMicros)} this month (estimated)`}
              </span>
              {MERCHANT_PAGE_ROLES.includes(user?.role) && (
                <Link to={`/admin/merchants/${sellerId}`}>Open store in Admin</Link>
              )}
            </div>
          )}
        </div>
        <AiUsagePeriodSelect value={period} onValueChange={setPeriod} />
      </div>

      {!store && !isLoading && (
        <p className="text-sm text-gray-500 dark:text-muted-foreground">
          Couldn't load this store.
        </p>
      )}

      {store && (
        <AiBudgetForm
          key={`${sellerId}-${store.overrideUsd}`}
          sellerId={sellerId}
          overrideUsd={store.overrideUsd}
          planBudgetMicros={store.planBudgetMicros}
        />
      )}

      {(store || isLoading) && (
        <div className="space-y-4">
          <h2>Users</h2>
          <DataGrid
            columns={columns}
            data={store?.users ?? []}
            isLoading={isLoading}
            paginationMode="page"
            getRowId={row => row.userId}
            emptyState={
              <EmptyState>
                <EmptyState.Title>No one used AI in this period</EmptyState.Title>
              </EmptyState>
            }
          />
        </div>
      )}

      <div className="space-y-4">
        <h2>Turns</h2>
        <AiUsageTurnsTable filters={{ ...range, seller: sellerId }} period={period} />
      </div>
    </div>
  );
};

export default AiUsageStore;
